import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Package, Clock, Infinity } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StartRentalDialog({ open, onClose, availableUnits, preSelectedUnit, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [rentalMode, setRentalMode] = useState('durasi'); // 'paket', 'durasi', 'no_limit'
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [durationUnit, setDurationUnit] = useState('hours'); // 'minutes' or 'hours'
  const [durationValue, setDurationValue] = useState(1); // default 1 hour
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    ps_unit_id: "",
    package_id: "",
    branch: "Cabang A"
  });

  // Set unit jika ada preSelectedUnit
  useEffect(() => {
    if (open && preSelectedUnit) {
      setFormData(prev => ({
        ...prev,
        ps_unit_id: preSelectedUnit.id
      }));
    }
  }, [open, preSelectedUnit]);

  useEffect(() => {
    if (open) {
      fetchPackages();
    }
  }, [open]);

  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/packages`, { headers });
      setPackages(response.data || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_name || !formData.ps_unit_id) {
      toast.error("Nama customer dan PS unit harus diisi");
      return;
    }

    // Validation based on rental mode
    if (rentalMode === 'paket' && !formData.package_id) {
      toast.error("Pilih paket terlebih dahulu");
      return;
    }

    try {
      setLoading(true);

      const selectedUnit = availableUnits.find(u => u.id === formData.ps_unit_id);
      
      // Get operator info from localStorage
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : {};
      
      let requestData = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        ps_unit_id: formData.ps_unit_id,
        rental_mode: rentalMode,
        branch: formData.branch,
        operator_name: currentUser.full_name || currentUser.username || 'Unknown',
        operator_role: currentUser.role || 'kasir'
      };

      // Add mode-specific data
      if (rentalMode === 'paket') {
        const selectedPackage = packages.find(p => p.id === formData.package_id);
        if (!selectedPackage) {
          toast.error("Paket tidak valid");
          return;
        }
        requestData.package_id = selectedPackage.id;
        requestData.package_name = selectedPackage.name;
        requestData.package_price = selectedPackage.price;
        requestData.duration_hours = selectedPackage.duration_hours;
      } else if (rentalMode === 'durasi') {
        const durationHours = durationUnit === 'hours' ? durationValue : durationValue / 60;
        requestData.duration_hours = durationHours;
        requestData.price_per_hour = selectedUnit?.price_per_hour;
      } else if (rentalMode === 'no_limit') {
        requestData.price_per_hour = selectedUnit?.price_per_hour;
      }

      const response = await axios.post(`${API}/rentals/start`, requestData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success(` Rental ${selectedUnit?.name || response.data.rental?.ps_unit_name || 'PS Unit'} berhasil dimulai!`, {
          duration: 3000,
          style: {
            background: 'linear-gradient(to right, #10b981, #059669)',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '16px',
            border: '2px solid #34d399',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)'
          }
        });
        // Reset form
        setFormData({
          customer_name: "",
          customer_phone: "",
          ps_unit_id: "",
          package_id: "",
          branch: "Cabang A"
        });
        setDurationUnit('hours');
        setDurationValue(1);
        setRentalMode('durasi');
        
        onSuccess();
        onClose();
      } else {
        toast.error(response.data.error || "Gagal memulai rental");
      }
    } catch (error) {
      console.error("Error starting rental:", error);
      toast.error(error.response?.data?.detail || "Gagal memulai rental");
    } finally {
      setLoading(false);
    }
  };

  const selectedUnit = availableUnits.find(u => u.id === formData.ps_unit_id);
  const selectedPackage = packages.find(p => p.id === formData.package_id);
  
  // Calculate display info based on mode
  const pricePerHour = selectedUnit?.price_per_hour || 0;
  const durationHours = durationUnit === 'hours' ? durationValue : durationValue / 60;
  
  let displayInfo = { duration: '', price: 0, priceLabel: '' };
  
  if (rentalMode === 'paket' && selectedPackage) {
    const hours = Math.floor(selectedPackage.duration_hours);
    const minutes = Math.round((selectedPackage.duration_hours - hours) * 60);
    displayInfo.duration = minutes > 0 ? `${hours} jam ${minutes} menit` : `${hours} jam`;
    displayInfo.price = selectedPackage.price;
    displayInfo.priceLabel = 'Harga Paket';
  } else if (rentalMode === 'durasi') {
    const hours = Math.floor(durationHours);
    const minutes = Math.round((durationHours - hours) * 60);
    displayInfo.duration = minutes > 0 ? `${hours} jam ${minutes} menit` : `${hours} jam`;
    displayInfo.price = durationHours * pricePerHour;
    displayInfo.priceLabel = 'Estimasi Harga';
  } else if (rentalMode === 'no_limit') {
    displayInfo.duration = 'Unlimited';
    displayInfo.price = 0;
    displayInfo.priceLabel = `Rp ${pricePerHour.toLocaleString('id-ID')}/jam`;
  }
  
  // Filter packages by selected unit's PS type
  const filteredPackages = selectedUnit 
    ? packages.filter(p => p.ps_type === selectedUnit.ps_type && p.branch_id === selectedUnit.branch_id)
    : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="billing-transaction-dialog sm:max-w-md max-h-[90vh] overflow-y-auto scrollbar-hide">
        <DialogHeader>
          <DialogTitle>Mulai Rental Baru</DialogTitle>
          <DialogDescription className="text-gray-600">
            Isi data customer dan pilih PS unit
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Customer Name */}
          <div>
            <Label htmlFor="customer_name">Nama Customer *</Label>
            <Input
              id="customer_name"
              placeholder="Masukkan nama customer"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              required
            />
          </div>

          {/* Customer Phone */}
          <div>
            <Label htmlFor="customer_phone">No. HP (Optional)</Label>
            <Input
              id="customer_phone"
              placeholder="08xx xxxx xxxx"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
            />
          </div>

          {/* PS Unit Selection */}
          <div>
            <Label htmlFor="ps_unit">PS Unit *</Label>
            {preSelectedUnit ? (
              // Unit sudah dipilih dari card - tidak bisa diganti
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-2xl"></span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-bold text-lg">{preSelectedUnit.name}</p>
                    <p className="text-gray-600 text-sm">{preSelectedUnit.ps_type}</p>
                    <p className="text-green-600 text-sm font-semibold">
                      Rp {preSelectedUnit.price_per_hour?.toLocaleString('id-ID') || 0}/jam
                    </p>
                  </div>
                  <div className="text-green-600 text-xs font-bold bg-green-100 px-3 py-1 rounded-full">
                     TERPILIH
                  </div>
                </div>
              </div>
            ) : (
              // Pilih unit manual
              <Select
                value={formData.ps_unit_id}
                onValueChange={(value) => {
                  setFormData({ ...formData, ps_unit_id: value, package_id: '' });
                }}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih PS" />
                </SelectTrigger>
                <SelectContent>
                  {availableUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name} ({unit.ps_type}) - Rp {unit.price_per_hour?.toLocaleString('id-ID') || 0}/jam
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedUnit && !selectedUnit.price_per_hour && (
              <p className="text-xs text-yellow-600 mt-1"> Unit belum di-set harga</p>
            )}
          </div>

          {/* Rental Mode Selection */}
          <div className="space-y-2">
            <Label>Mode Rental *</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setRentalMode('paket')}
                className={`p-3 rounded-lg border transition flex flex-col items-center gap-1 ${
                  rentalMode === 'paket'
                    ? 'bg-green-600 border-green-500 text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Package className="h-5 w-5" />
                <span className="text-xs font-medium">Paket</span>
              </button>
              <button
                type="button"
                onClick={() => setRentalMode('durasi')}
                className={`p-3 rounded-lg border transition flex flex-col items-center gap-1 ${
                  rentalMode === 'durasi'
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Clock className="h-5 w-5" />
                <span className="text-xs font-medium">Durasi</span>
              </button>
              <button
                type="button"
                onClick={() => setRentalMode('no_limit')}
                className={`p-3 rounded-lg border transition flex flex-col items-center gap-1 ${
                  rentalMode === 'no_limit'
                    ? 'bg-purple-600 border-purple-500 text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Infinity className="h-5 w-5" />
                <span className="text-xs font-medium">No Limit</span>
              </button>
            </div>
          </div>

          {/* Conditional Fields Based on Mode */}
          {rentalMode === 'paket' && (
            <div>
              <Label htmlFor="package">Pilih Paket *</Label>
              {loadingPackages ? (
                <div className="p-4 text-center text-gray-500">Loading paket...</div>
              ) : filteredPackages.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-xs text-yellow-700">Belum ada paket untuk unit ini</p>
                </div>
              ) : (
                <Select
                  value={formData.package_id}
                  onValueChange={(value) => setFormData({ ...formData, package_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Paket" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPackages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id}>
                        {pkg.name} - {pkg.duration_hours}h - Rp {pkg.price.toLocaleString('id-ID')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {rentalMode === 'durasi' && (
            <div className="space-y-3">
              <Label>Durasi Rental</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setDurationUnit('hours'); setDurationValue(1); }}
                  className={`flex-1 px-4 py-2 rounded-lg transition ${
                    durationUnit === 'hours' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Jam
                </button>
                <button
                  type="button"
                  onClick={() => { setDurationUnit('minutes'); setDurationValue(30); }}
                  className={`flex-1 px-4 py-2 rounded-lg transition ${
                    durationUnit === 'minutes' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Menit
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {durationUnit === 'hours' ? (
                  [1, 2, 3, 4].map(h => (
                    <button key={h} type="button" onClick={() => setDurationValue(h)}
                      className={`px-3 py-2 rounded-lg text-sm ${durationValue === h ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {h}h
                    </button>
                  ))
                ) : (
                  [15, 30, 45, 60].map(m => (
                    <button key={m} type="button" onClick={() => setDurationValue(m)}
                      className={`px-3 py-2 rounded-lg text-sm ${durationValue === m ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {m}m
                    </button>
                  ))
                )}
              </div>
              <Input
                type="number"
                value={durationValue}
                onChange={(e) => setDurationValue(parseFloat(e.target.value) || 0)}
                min={durationUnit === 'hours' ? 0.5 : 5}
                step={durationUnit === 'hours' ? 0.5 : 5}
              />
            </div>
          )}

          {rentalMode === 'no_limit' && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700">
                 Mode No Limit: Billing berjalan sampai dihentikan manual. 
                Charge per jam (rounded up).
              </p>
            </div>
          )}

          {/* Price Display */}
          {selectedUnit && selectedUnit.price_per_hour && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-blue-700">{displayInfo.priceLabel}:</span>
                <span className="text-2xl font-bold text-blue-900">
                  {displayInfo.price > 0 ? `Rp ${displayInfo.price.toLocaleString('id-ID')}` : displayInfo.priceLabel}
                </span>
              </div>
              {displayInfo.duration && (
                <p className="text-xs text-blue-600 mt-1">
                  Durasi: {displayInfo.duration}
                </p>
              )}
            </div>
          )}
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Batal
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700"
            disabled={loading || !formData.customer_name || !formData.ps_unit_id}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Memulai...
              </>
            ) : (
              "Mulai Rental"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
